import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Trash2, Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Archive,
  CalendarRange, Mail, Bell, Inbox, MessageSquare, FileBarChart, UserCheck,
  Tablet, IdCard, Link as LinkIcon, HardHat, User as UserIcon, FileWarning,
  FileSignature, DoorOpen,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

/**
 * Phase B5 — Shared Data Cleanup sections.
 *
 * Used by:
 *   • /settings/data         (org admin, own-org)
 *   • /super-admin/data-cleanup (super admin, picks any org)
 *
 * Sections:
 *   A. Quick clean — onboarding sample data (hidden when count = 0).
 *   B. Clear by collection.
 *   D. Full org reset (super admin only).
 *
 * The brief's "Section C — Information panel" is rendered by the host
 * page so the message wording can differ between the two surfaces.
 */
const ICONS = {
  CalendarRange, Mail, Bell, Inbox, MessageSquare, FileBarChart, UserCheck,
  Tablet, IdCard, Link: LinkIcon, HardHat, User: UserIcon, FileWarning,
  FileSignature, DoorOpen, Trash2,
};
const Icon = ({ name, className }) => {
  const Cmp = ICONS[name] || Trash2;
  return <Cmp className={className} />;
};


function ConfirmDialog({ open, onOpenChange, expected, expectedType, scopeLabel, total, isLoading, onConfirm }) {
  const [input, setInput] = useState("");
  useEffect(() => { if (!open) setInput(""); }, [open]);
  const matched = input === expected;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="data-cleanup-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Hard delete — confirm
          </AlertDialogTitle>
          <AlertDialogDescription>
            About to clear <strong>{total}</strong> document{total === 1 ? "" : "s"} from {scopeLabel}.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <label className="text-xs font-medium text-navy-600 dark:text-navy-200">
            {expectedType === "type_org_name"
              ? <>Type the organisation name <strong>verbatim</strong> to confirm:</>
              : <>Type <strong>DELETE</strong> to confirm:</>}
          </label>
          <div className="bg-navy-50 dark:bg-navy-900 border border-navy-200 dark:border-navy-700 rounded-md px-3 py-2 text-sm font-mono select-none text-navy-700 dark:text-navy-100" data-testid="data-cleanup-expected">
            {expected}
          </div>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={expectedType === "type_org_name" ? "Type the organisation name…" : "Type DELETE"}
            className="w-full px-3 py-2 border border-navy-200 dark:border-navy-700 rounded-md text-sm bg-white dark:bg-navy-800"
            data-testid="data-cleanup-confirm-input"
            autoFocus
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="data-cleanup-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!matched || isLoading}
            onClick={onConfirm}
            className="bg-destructive hover:bg-destructive/90 disabled:opacity-50"
            data-testid="data-cleanup-execute"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Clear data
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


export default function DataCleanupSections({ targetOrgId, scopeIsSuperAdmin, focusSample }) {
  const { formatDate } = useLocale();
  const [registry, setRegistry] = useState(null);
  const [samplePreview, setSamplePreview] = useState(null);
  const [pickerPreview, setPickerPreview] = useState(null);
  const [fullOrgPreview, setFullOrgPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [sourceTagInput, setSourceTagInput] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);   // "sample" | "picker" | "full"
  // Phase B5.1 — snapshot-before-wipe checkbox (super_admin full_org only).
  const [takeSnapshot, setTakeSnapshot] = useState(false);

  const loadRegistry = useCallback(async () => {
    try {
      const { data } = await api.get("/data-cleanup/registry");
      setRegistry(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const refreshSamplePreview = useCallback(async () => {
    try {
      const { data } = await api.post("/data-cleanup/preview", {
        org_id: targetOrgId,
        scope: "by_source_tag",
        source_tag: "onboarding_sample",
      });
      setSamplePreview(data);
    } catch (e) {
      // Silent: org_admin without onboarding_sample data → empty card
      // is the desired UX, not an error toast.
      setSamplePreview(null);
    }
  }, [targetOrgId]);

  const refreshFullOrgPreview = useCallback(async () => {
    if (!scopeIsSuperAdmin) return;
    try {
      const { data } = await api.post("/data-cleanup/preview", {
        org_id: targetOrgId,
        scope: "full_org",
      });
      setFullOrgPreview(data);
    } catch (e) { /* org with 0 docs returns empty preview, no toast */ }
  }, [scopeIsSuperAdmin, targetOrgId]);

  useEffect(() => { loadRegistry(); }, [loadRegistry]);
  useEffect(() => {
    if (!targetOrgId) return;
    refreshSamplePreview();
    refreshFullOrgPreview();
  }, [targetOrgId, refreshSamplePreview, refreshFullOrgPreview]);

  // Auto-scroll if the user landed via "Clear onboarding sample data" Help menu link.
  useEffect(() => {
    if (!focusSample) return;
    const el = document.querySelector('[data-testid="cleanup-sample-card"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusSample, samplePreview]);

  // Picker preview — runs when the user clicks "Preview".
  const runPickerPreview = useCallback(async () => {
    const keys = Object.keys(selected).filter((k) => selected[k]);
    if (keys.length === 0) {
      toast.error("Select at least one collection.");
      return;
    }
    setPickerLoading(true);
    try {
      const { data } = await api.post("/data-cleanup/preview", {
        org_id: targetOrgId,
        scope: "by_collection",
        collections: keys,
        source_tag: sourceTagInput.trim() || null,
      });
      setPickerPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPickerLoading(false); }
  }, [selected, sourceTagInput, targetOrgId]);

  // Execute helpers — each returns a function bound to the right scope.
  const executeSample = async () => {
    if (!samplePreview) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/data-cleanup/execute", {
        org_id: targetOrgId,
        scope: "by_source_tag",
        source_tag: "onboarding_sample",
        confirmation_input: samplePreview.confirmation_required.value,
      });
      toast.success(`Cleared ${data.deleted} sample document${data.deleted === 1 ? "" : "s"}.`);
      setConfirmKind(null);
      await refreshSamplePreview();
      await refreshFullOrgPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecuting(false); }
  };

  const executePicker = async () => {
    if (!pickerPreview) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/data-cleanup/execute", {
        org_id: targetOrgId,
        scope: "by_collection",
        collections: pickerPreview.counts.filter((c) => c.count > 0).map((c) => c.key),
        source_tag: sourceTagInput.trim() || null,
        confirmation_input: pickerPreview.confirmation_required.value,
      });
      toast.success(`Cleared ${data.deleted} document${data.deleted === 1 ? "" : "s"} across ${Object.keys(data.per_collection).length} collection(s).`);
      setConfirmKind(null);
      setSelected({});
      setPickerPreview(null);
      await refreshSamplePreview();
      await refreshFullOrgPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecuting(false); }
  };

  const executeFullOrg = async () => {
    if (!fullOrgPreview) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/data-cleanup/execute", {
        org_id: targetOrgId,
        scope: "full_org",
        confirmation_input: fullOrgPreview.confirmation_required.value,
        take_snapshot: takeSnapshot,
      });
      if (data.snapshot_url) {
        toast.success(
          `Org wiped — ${data.deleted} doc(s) cleared. Snapshot saved (${(data.snapshot_size_bytes / 1024).toFixed(1)} KB, expires ${formatDate(data.snapshot_expires_at)}).`,
          { duration: 10000 },
        );
      } else {
        toast.success(`Org wiped — ${data.deleted} document(s) cleared.`);
      }
      setConfirmKind(null);
      setTakeSnapshot(false);
      await refreshSamplePreview();
      await refreshFullOrgPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecuting(false); }
  };

  if (!registry) {
    return (
      <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-6 text-sm text-navy-500 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading collections…
      </div>
    );
  }

  const operational = registry.registry.filter((r) => r.category === "operational");
  const compliance  = registry.registry.filter((r) => r.category === "compliance");
  const configuration = registry.registry.filter((r) => r.category === "configuration");

  return (
    <div className="space-y-6">
      {/* ───────── Section A — Quick clean ───────── */}
      {samplePreview && samplePreview.total > 0 && (
        <section
          className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-navy-800 dark:to-navy-800 border border-orange-200 dark:border-orange-700 rounded-xl p-5"
          data-testid="cleanup-sample-card"
        >
          <div className="flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-orange-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-navy-900 dark:text-white">Clear onboarding sample data</h2>
              <p className="text-sm text-navy-600 dark:text-navy-200 mt-1">
                Wipe the sample seed loaded during the onboarding tour. Sample-tagged docs only — your real data stays put.
              </p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cleanup-sample-counts">
                {samplePreview.counts.filter((c) => c.count > 0).map((c) => (
                  <div key={c.key} className="bg-white dark:bg-navy-900 border border-navy-100 dark:border-navy-700 rounded-md px-3 py-2 text-xs">
                    <div className="text-navy-500 dark:text-navy-400">{c.label}</div>
                    <div className="font-semibold text-navy-900 dark:text-white">{c.count}</div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setConfirmKind("sample")}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
                data-testid="cleanup-sample-btn"
              >
                <Trash2 className="w-4 h-4" /> Clear sample data ({samplePreview.total})
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ───────── Section B — Clear by collection ───────── */}
      <section
        className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-5"
        data-testid="cleanup-picker"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-navy-900 dark:text-white">Clear by collection</h2>
            <p className="text-xs text-navy-500 dark:text-navy-300 mt-1">Select one or more collections to wipe.</p>
          </div>
          <button
            type="button"
            onClick={() => { refreshSamplePreview(); refreshFullOrgPreview(); }}
            className="text-xs text-navy-500 hover:text-orange-600 inline-flex items-center gap-1"
            data-testid="cleanup-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh counts
          </button>
        </div>

        <CollectionPickerGroup title="Operational" rows={operational} selected={selected} setSelected={setSelected} />
        <CollectionPickerGroup title="Compliance" rows={compliance} selected={selected} setSelected={setSelected} />

        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-navy-500 hover:text-orange-600"
          data-testid="cleanup-advanced-toggle"
        >
          {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {advancedOpen ? "Hide advanced" : "Show advanced (configuration data)"}
        </button>
        {advancedOpen && (
          <CollectionPickerGroup title="Configuration (advanced)" rows={configuration} selected={selected} setSelected={setSelected} className="mt-3" />
        )}

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-navy-600 dark:text-navy-200">
              Source tag filter (optional — restricts to docs with this `source` value)
            </label>
            <input
              type="text"
              value={sourceTagInput}
              onChange={(e) => setSourceTagInput(e.target.value)}
              placeholder="e.g. onboarding_sample"
              className="mt-1 w-full px-3 py-2 border border-navy-200 dark:border-navy-700 rounded-md text-sm bg-white dark:bg-navy-900"
              data-testid="cleanup-source-tag-input"
            />
          </div>
          <div className="flex items-end justify-end gap-2">
            <button
              type="button"
              onClick={runPickerPreview}
              disabled={pickerLoading}
              className="px-4 py-2 rounded-md border border-navy-200 dark:border-navy-700 text-sm font-medium text-navy-700 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700 disabled:opacity-50"
              data-testid="cleanup-preview-btn"
            >
              {pickerLoading ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
              Preview
            </button>
            <button
              type="button"
              onClick={() => {
                if (!pickerPreview) {
                  toast.error("Run preview first.");
                  return;
                }
                setConfirmKind("picker");
              }}
              disabled={!pickerPreview || pickerPreview.total === 0}
              className="px-4 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="cleanup-execute-btn"
            >
              <Trash2 className="w-4 h-4 inline mr-1" /> Clear data
            </button>
          </div>
        </div>

        {pickerPreview && (
          <div className="mt-4 bg-navy-50 dark:bg-navy-900 border border-navy-100 dark:border-navy-700 rounded-md p-3" data-testid="cleanup-preview-summary">
            <div className="text-xs font-semibold text-navy-700 dark:text-navy-100 mb-1">
              Will delete <span className="text-destructive font-bold">{pickerPreview.total}</span> document{pickerPreview.total === 1 ? "" : "s"} across {pickerPreview.counts.filter((c) => c.count > 0).length} collection(s):
            </div>
            <ul className="space-y-0.5 text-xs text-navy-600 dark:text-navy-200">
              {pickerPreview.counts.map((c) => (
                <li key={c.key} className="flex justify-between gap-3">
                  <span>{c.label}</span>
                  <span className="font-mono">{c.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ───────── Section D — Full org reset (super_admin only) ───────── */}
      {scopeIsSuperAdmin && (
        <section
          className="bg-white dark:bg-navy-800 border-2 border-destructive/40 rounded-xl p-5"
          data-testid="cleanup-full-org"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-destructive text-white text-[10px] uppercase tracking-wider font-bold rounded">
              Destructive — full org
            </span>
            <h2 className="text-base font-semibold text-navy-900 dark:text-white">Full org reset</h2>
          </div>
          <p className="text-xs text-navy-500 dark:text-navy-300 mb-3">
            Clears every deletable collection at once. Preserves: users, sites, branding,
            subscription, audit log, KB articles, calendar connections.
          </p>
          {fullOrgPreview && (
            <div className="bg-destructive/5 border border-destructive/30 rounded-md p-3 mb-3">
              <div className="text-xs font-semibold text-destructive">
                {fullOrgPreview.total} document{fullOrgPreview.total === 1 ? "" : "s"} will be cleared across {fullOrgPreview.counts.filter((c) => c.count > 0).length} collection(s).
              </div>
            </div>
          )}
          <label className="flex items-start gap-2 mb-3 cursor-pointer" data-testid="cleanup-snapshot-toggle">
            <input
              type="checkbox"
              checked={takeSnapshot}
              onChange={(e) => setTakeSnapshot(e.target.checked)}
              className="mt-0.5 accent-orange-500"
              data-testid="cleanup-snapshot-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-navy-900 dark:text-white inline-flex items-center gap-1.5">
                <Archive className="w-3.5 h-3.5 text-navy-500" />
                Export snapshot to JSON before deleting (30-day TTL)
              </div>
              <div className="text-[11px] text-navy-500 dark:text-navy-300 mt-0.5">
                Writes a downloadable JSON file of every doc about to be deleted. If the snapshot upload fails the wipe is aborted.
              </div>
            </div>
          </label>
          <button
            type="button"
            onClick={() => {
              if (!fullOrgPreview || fullOrgPreview.total === 0) {
                toast.error("Nothing to wipe — preview is empty.");
                return;
              }
              setConfirmKind("full");
            }}
            disabled={!fullOrgPreview || fullOrgPreview.total === 0}
            className="px-4 py-2 rounded-md bg-destructive hover:bg-destructive/90 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="cleanup-full-org-btn"
          >
            <AlertTriangle className="w-4 h-4 inline mr-1" /> Wipe org data
          </button>
        </section>
      )}

      <ConfirmDialog
        open={confirmKind === "sample"}
        onOpenChange={(v) => !v && setConfirmKind(null)}
        expected={samplePreview?.confirmation_required?.value || "DELETE"}
        expectedType={samplePreview?.confirmation_required?.type || "type_word"}
        scopeLabel={samplePreview?.confirmation_required?.scope_label || "your organisation's data"}
        total={samplePreview?.total || 0}
        isLoading={executing}
        onConfirm={executeSample}
      />
      <ConfirmDialog
        open={confirmKind === "picker"}
        onOpenChange={(v) => !v && setConfirmKind(null)}
        expected={pickerPreview?.confirmation_required?.value || "DELETE"}
        expectedType={pickerPreview?.confirmation_required?.type || "type_word"}
        scopeLabel={pickerPreview?.confirmation_required?.scope_label || "your organisation's data"}
        total={pickerPreview?.total || 0}
        isLoading={executing}
        onConfirm={executePicker}
      />
      <ConfirmDialog
        open={confirmKind === "full"}
        onOpenChange={(v) => !v && setConfirmKind(null)}
        expected={fullOrgPreview?.confirmation_required?.value || ""}
        expectedType={fullOrgPreview?.confirmation_required?.type || "type_org_name"}
        scopeLabel={fullOrgPreview?.confirmation_required?.scope_label || "this organisation"}
        total={fullOrgPreview?.total || 0}
        isLoading={executing}
        onConfirm={executeFullOrg}
      />
    </div>
  );
}


function CollectionPickerGroup({ title, rows, selected, setSelected, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-navy-400 mt-2 mb-1">{title}</div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.key}>
            <label
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm cursor-pointer ${
                r.non_deletable
                  ? "bg-navy-50 dark:bg-navy-900 cursor-not-allowed opacity-60"
                  : "hover:bg-navy-50 dark:hover:bg-navy-700"
              }`}
              data-testid={`cleanup-pick-${r.key}`}
            >
              <input
                type="checkbox"
                checked={!!selected[r.key] && !r.non_deletable}
                disabled={r.non_deletable}
                onChange={(e) => setSelected((s) => ({ ...s, [r.key]: e.target.checked }))}
                className="accent-orange-500"
              />
              <Icon name={r.icon} className="w-4 h-4 text-navy-400" />
              <span className="flex-1 text-navy-700 dark:text-navy-100">{r.label}</span>
              {r.non_deletable && (
                <span className="text-[10px] uppercase tracking-wider text-navy-400" title={r.rationale}>
                  preserved
                </span>
              )}
              {r.supports_source_filter && !r.non_deletable && (
                <span className="text-[10px] uppercase tracking-wider text-orange-500" title="Supports source-tag filter">
                  tag
                </span>
              )}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
